/**
 * Browser-side device identity for OpenClaw gateway pairings.
 *
 * Implements the OpenClaw v2 device-auth handshake (see
 * https://github.com/openclaw/openclaw — `buildDeviceAuthPayload` in
 * `src/gateway/device-auth.ts`):
 *
 *   - Ed25519 keypair; `deviceId = SHA-256(rawPublicKey).hex` — must match
 *     the server's `deriveDeviceIdFromPublicKey()` exactly.
 *   - Signature is over the canonical v2 payload string:
 *       `v2|deviceId|clientId|clientMode|role|scopes|signedAtMs|token|nonce`
 *     UTF-8 encoded; the signature is the raw 64-byte Ed25519 output,
 *     base64-encoded.
 *   - `signedAt` is Unix epoch **milliseconds** (integer).
 *
 * The gateway verifies `device: { id, publicKey, signature, signedAt, nonce }`
 * on each connect, letting it rotate pairing tokens without a re-approval.
 *
 * ## Key storage
 *
 * The device private key is a signing secret: anything that can read it can
 * forge device handshakes and silently obtain fresh operator tokens. We
 * therefore prefer a **non-extractable** WebCrypto Ed25519 `CryptoKey`
 * persisted in IndexedDB — the raw private bytes never exist in JS and cannot
 * be read by same-origin XSS or a browser extension. Only the public key
 * (safe to expose) is kept as raw bytes.
 *
 * Browsers without WebCrypto Ed25519 fall back to the legacy `@noble/curves`
 * path, which keeps the raw private key in localStorage. That is strictly the
 * previous behavior; it is used only when the hardened path is unavailable,
 * and pre-existing legacy identities continue to load so users aren't logged
 * out. The wire protocol is identical on both paths.
 *
 * @note Target audience: Paradigm/Paradex traders. Communication should be
 * concise and information-dense unless specifically asked for explanation.
 */

import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

import {
  clearLegacyDeviceKey,
  migrateLegacyDeviceKey,
} from './device-identity-migration';

const STORAGE_KEY = 'paradigm_terminal_device';

/** Version tag for the signed auth payload. */
const DEVICE_AUTH_PAYLOAD_VERSION = 'v2';

// IndexedDB store for the non-extractable WebCrypto identity.
const IDB_NAME = 'paradigm_terminal_device';
const IDB_STORE = 'identity';
const IDB_KEY = 'device';

interface StoredDevice {
  readonly id: string; // hex SHA-256 of raw public key
  readonly privateKey: string; // base64 of raw 32 bytes (legacy path only)
  readonly publicKey: string; // base64 of raw 32 bytes
}

/** Record persisted in IndexedDB for the hardened (WebCrypto) path. */
export interface StoredCryptoKeyDevice {
  readonly id: string;
  readonly publicKey: string; // base64 of raw 32-byte public key
  readonly privateKey: CryptoKey; // non-extractable; structured-cloned by IDB
}

export interface DeviceIdentity {
  readonly id: string;
  /** Base64-encoded raw 32-byte Ed25519 public key. */
  readonly publicKey: string;
  /** Signs a UTF-8 payload; returns base64 of the raw 64-byte signature. */
  readonly sign: (payload: string) => Promise<string>;
}

let cached: Promise<DeviceIdentity> | null = null;

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCodePoint(b);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.codePointAt(i)!;
  return out;
}

export async function getDeviceIdentity(): Promise<DeviceIdentity> {
  if (cached == null) {
    const pending = loadOrCreate();
    // Don't cache a rejection — a transient failure would otherwise poison
    // every future call for the lifetime of the page. Let the next call retry.
    pending.catch(() => {
      if (cached === pending) cached = null;
    });
    cached = pending;
  }
  return cached;
}

/** Reset the in-memory cache. Test-only. */
export function resetDeviceIdentityCacheForTests(): void {
  cached = null;
}

async function loadOrCreate(): Promise<DeviceIdentity> {
  // Prefer the hardened WebCrypto path when the browser supports Ed25519.
  if (await isWebCryptoEd25519Supported()) {
    try {
      return await loadOrCreateWebCrypto();
    } catch {
      // Fall through to the legacy path if IndexedDB is unavailable
      // (e.g. private-browsing quirks) so signing still works this session.
    }
  }
  return loadOrCreateLegacy();
}

// --- Hardened path: non-extractable WebCrypto key in IndexedDB ---

async function loadOrCreateWebCrypto(): Promise<DeviceIdentity> {
  const existing = await idbGet();
  if (
    existing != null &&
    typeof existing.id === 'string' &&
    typeof existing.publicKey === 'string' &&
    existing.privateKey instanceof CryptoKey
  ) {
    return cryptoKeyIdentity(existing);
  }

  // No hardened key yet. Reuse an existing legacy localStorage key if present
  // (preserving deviceId, so no re-pairing); otherwise mint a fresh one.
  const migrated = await migrateLegacyDeviceKey();
  const record = migrated ?? (await generateFreshDeviceRecord());
  await idbPut(record);
  // Only after the key is safely in IndexedDB do we remove the raw legacy copy.
  if (migrated != null) clearLegacyDeviceKey();
  return cryptoKeyIdentity(record);
}

async function generateFreshDeviceRecord(): Promise<StoredCryptoKeyDevice> {
  // This lib's generateKey overload returns CryptoKey | CryptoKeyPair; for an
  // asymmetric algorithm it is always a CryptoKeyPair.
  const pair = (await crypto.subtle.generateKey({ name: 'Ed25519' }, false, [
    'sign',
    'verify',
  ])) as CryptoKeyPair;
  const rawPub = new Uint8Array(
    await crypto.subtle.exportKey('raw', pair.publicKey),
  );
  return {
    id: bytesToHex(sha256(rawPub)),
    publicKey: bytesToBase64(rawPub),
    privateKey: pair.privateKey, // extractable:false — bytes never leave WebCrypto
  };
}

function cryptoKeyIdentity(record: StoredCryptoKeyDevice): DeviceIdentity {
  return {
    id: record.id,
    publicKey: record.publicKey,
    async sign(payload: string): Promise<string> {
      const sig = new Uint8Array(
        await crypto.subtle.sign(
          'Ed25519',
          record.privateKey,
          new TextEncoder().encode(payload),
        ),
      );
      return bytesToBase64(sig);
    },
  };
}

async function isWebCryptoEd25519Supported(): Promise<boolean> {
  // Read through globalThis so the runtime guards aren't flagged as
  // unnecessary — lib.dom types crypto.subtle/indexedDB as always defined,
  // but they can be absent in non-secure contexts or private-mode quirks.
  const g = globalThis as {
    crypto?: { subtle?: unknown };
    indexedDB?: unknown;
  };
  if (g.crypto?.subtle == null || g.indexedDB == null) {
    return false;
  }
  try {
    // The only reliable feature test is to actually generate a key — some
    // engines expose subtle but reject the Ed25519 algorithm at runtime.
    await crypto.subtle.generateKey({ name: 'Ed25519' }, false, [
      'sign',
      'verify',
    ]);
    return true;
  } catch {
    return false;
  }
}

async function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) {
        req.result.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

async function idbGet(): Promise<StoredCryptoKeyDevice | null> {
  const db = await idbOpen();
  try {
    return await new Promise<StoredCryptoKeyDevice | null>(
      (resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
        req.onsuccess = () =>
          resolve((req.result as StoredCryptoKeyDevice | undefined) ?? null);
        req.onerror = () => reject(req.error ?? new Error('idb get failed'));
      },
    );
  } finally {
    db.close();
  }
}

async function idbPut(record: StoredCryptoKeyDevice): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(record, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('idb put failed'));
    });
  } finally {
    db.close();
  }
}

// --- Legacy path: raw key in localStorage (@noble) ---

function loadOrCreateLegacy(): DeviceIdentity {
  let stored: StoredDevice | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw != null) stored = JSON.parse(raw) as StoredDevice;
  } catch {
    stored = null;
  }

  // Decode the stored private key up front so a corrupt-but-string value
  // (invalid base64 → atob throws) is treated as "no usable key" and
  // regenerated, rather than throwing out of this function.
  let privBytes = stored != null ? tryDecodeSeed(stored.privateKey) : null;

  // Refuse entries that are missing required fields (covers migrations from
  // older JWK-based storage) or whose key didn't decode to a 32-byte seed.
  if (
    stored == null ||
    typeof stored.id !== 'string' ||
    typeof stored.publicKey !== 'string' ||
    privBytes == null
  ) {
    const privateKey = ed25519.utils.randomPrivateKey();
    const publicKey = ed25519.getPublicKey(privateKey);
    const id = bytesToHex(sha256(publicKey));
    stored = {
      id,
      privateKey: bytesToBase64(privateKey),
      publicKey: bytesToBase64(publicKey),
    };
    privBytes = privateKey;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Non-persistent identity; signing still works for this session.
    }
  }

  const seed = privBytes;
  return {
    id: stored.id,
    publicKey: stored.publicKey,
    async sign(payload: string): Promise<string> {
      const sig = ed25519.sign(new TextEncoder().encode(payload), seed);
      return bytesToBase64(sig);
    },
  };
}

/** Decode a base64 Ed25519 seed, returning null if invalid or wrong length. */
function tryDecodeSeed(b64: unknown): Uint8Array | null {
  if (typeof b64 !== 'string') return null;
  try {
    const bytes = base64ToBytes(b64);
    return bytes.length === 32 ? bytes : null;
  } catch {
    return null;
  }
}

export interface DeviceHandshakeInput {
  readonly clientId: string;
  /** Client mode — e.g. `'webchat'` for the browser-based terminal. */
  readonly clientMode: string;
  /** Operator role — `'operator'` or `'pairing'`. */
  readonly role: string;
  /** Scopes as comma-joined string, in the exact order the client sends them. */
  readonly scopes: readonly string[];
  /** Auth token; empty string when in pairing mode (unpaired first connect). */
  readonly token: string;
  /** Server-provided nonce from `connect.challenge`. */
  readonly nonce: string;
}

export interface DeviceHandshake {
  readonly id: string;
  /** Base64-encoded raw 32-byte Ed25519 public key. */
  readonly publicKey: string;
  /** Base64-encoded raw 64-byte Ed25519 signature over the v2 payload. */
  readonly signature: string;
  /** Unix timestamp in milliseconds (integer). */
  readonly signedAt: number;
  readonly nonce: string;
}

/**
 * Build the signed `device` field for a gateway connect request.
 *
 * Canonical v2 signed payload:
 *   `v2|deviceId|clientId|clientMode|role|scopes|signedAtMs|token|nonce`
 */
export async function buildDeviceHandshake(
  input: DeviceHandshakeInput,
): Promise<DeviceHandshake> {
  const identity = await getDeviceIdentity();
  const signedAt = Date.now();
  const scopesField = input.scopes.join(',');

  const payload = [
    DEVICE_AUTH_PAYLOAD_VERSION,
    identity.id,
    input.clientId,
    input.clientMode,
    input.role,
    scopesField,
    String(signedAt),
    input.token,
    input.nonce,
  ].join('|');

  const signature = await identity.sign(payload);

  return {
    id: identity.id,
    publicKey: identity.publicKey,
    signature,
    signedAt,
    nonce: input.nonce,
  };
}
